import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

// Lightweight Assignment Group model stored in localStorage
// Group: { id, title, assignmentDocId: string|null, referenceDocIds: string[],
//          content: string, citations: [], settings: { autoCite: boolean, mode: 'qa'|'essay' },
//          lastModified: string }
// Docs come from localStorage key 'quizmate_files' (used by SidePanel)

const STORAGE_KEY = "quizmate_assignment_groups";
const ACTIVE_KEY = "quizmate_active_assignment_group_id";

const AssignmentContext = createContext(null);

export function AssignmentProvider({ children }) {
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);

  // Load on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      setGroups(Array.isArray(arr) ? arr : []);
    } catch {
      setGroups([]);
    }
    try {
      const a = localStorage.getItem(ACTIVE_KEY);
      setActiveGroupId(a || null);
    } catch {
      setActiveGroupId(null);
    }
  }, []);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
    } catch {}
  }, [groups]);
  useEffect(() => {
    try {
      if (activeGroupId) localStorage.setItem(ACTIVE_KEY, activeGroupId);
      else localStorage.removeItem(ACTIVE_KEY);
    } catch {}
  }, [activeGroupId]);

  const createGroup = useCallback((title = "Assignment Group") => {
    const id = `ag_${Date.now()}`;
    const g = {
      id,
      title,
      assignmentDocId: null,
      referenceDocIds: [],
      content: "",
      citations: [],
      settings: { autoCite: true, mode: "essay" },
      lastModified: new Date().toISOString(),
    };
    setGroups((prev) => [g, ...prev]);
    return id;
  }, []);

  const updateGroup = useCallback((id, patch) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, ...patch } : g))
    );
  }, []);

  const deleteGroup = useCallback((id) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));
    setActiveGroupId((prev) => (prev === id ? null : prev));
  }, []);

  const setActiveGroup = useCallback((id) => setActiveGroupId(id), []);

  // Role operations
  const setAssignmentDoc = useCallback(
    (groupId, docId) => {
      updateGroup(groupId, { assignmentDocId: docId });
    },
    [updateGroup]
  );

  const addReferenceDoc = useCallback((groupId, docId) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              referenceDocIds: Array.from(
                new Set([...(g.referenceDocIds || []), docId])
              ),
            }
          : g
      )
    );
  }, []);

  const removeReferenceDoc = useCallback((groupId, docId) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              referenceDocIds: (g.referenceDocIds || []).filter(
                (id) => id !== docId
              ),
            }
          : g
      )
    );
  }, []);

  const value = useMemo(
    () => ({
      groups,
      activeGroupId,
      activeGroup: groups.find((g) => g.id === activeGroupId) || null,
      createGroup,
      updateGroup,
      deleteGroup,
      setActiveGroup,
      setAssignmentDoc,
      addReferenceDoc,
      removeReferenceDoc,
    }),
    [
      groups,
      activeGroupId,
      createGroup,
      updateGroup,
      deleteGroup,
      setActiveGroup,
      setAssignmentDoc,
      addReferenceDoc,
      removeReferenceDoc,
    ]
  );

  return (
    <AssignmentContext.Provider value={value}>
      {children}
    </AssignmentContext.Provider>
  );
}

export function useAssignment() {
  const ctx = useContext(AssignmentContext);
  if (!ctx)
    throw new Error("useAssignment must be used within AssignmentProvider");
  return ctx;
}
